import { useMemo } from 'react';
import { Box, Chip, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { summariseTimezones } from '../shared/timezone';
import { useTeamMemberEntities } from '../shared/useTeamMemberEntities';

const COMPOSITION_FIELDS = ['kind', 'metadata.annotations'];

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2),
    '&:last-child': { marginBottom: 0 },
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontSize: '0.95rem',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

/**
 * Team-level rollup of PAT-422 user annotations: timezone span and office
 * distribution across the team's members. Renders nothing when there is no
 * timezone or office data to show, including when the group has no members.
 */
export function EntityTeamCompositionCard() {
  const classes = useStyles();
  const { items, loading, error } = useTeamMemberEntities(COMPOSITION_FIELDS, {
    includeLeadership: true,
  });

  const { timezones, offices } = useMemo(() => {
    const tzs: string[] = [];
    const offs: string[] = [];
    for (const u of items) {
      const a = u.metadata.annotations ?? {};
      if (a['user/timezone']) tzs.push(a['user/timezone']);
      if (a['user/office']) offs.push(a['user/office']);
    }
    return { timezones: tzs, offices: offs };
  }, [items]);

  const officeCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of offices) map.set(o, (map.get(o) ?? 0) + 1);
    // Sort by member count descending; deterministic tiebreaker on office
    // name so equal-count chips don't shuffle between renders.
    return Array.from(map.entries()).sort(
      ([nameA, countA], [nameB, countB]) =>
        countB - countA || nameA.localeCompare(nameB),
    );
  }, [offices]);

  const tzSummary = useMemo(() => summariseTimezones(timezones), [timezones]);

  const hasAnything = tzSummary !== undefined || officeCounts.length > 0;

  let body: JSX.Element;
  if (loading) {
    body = <Progress />;
  } else if (error) {
    body = <ResponseErrorPanel error={error} />;
  } else if (!hasAnything) {
    return null;
  } else {
    body = (
      <>
        {tzSummary && (
          <Box className={classes.section}>
            <Typography className={classes.label}>Timezones</Typography>
            <Typography className={classes.value}>{tzSummary}</Typography>
          </Box>
        )}
        {officeCounts.length > 0 && (
          <Box className={classes.section}>
            <Typography className={classes.label}>Offices</Typography>
            <Box className={classes.chipRow}>
              {officeCounts.map(([office, count]) => (
                <Chip
                  key={office}
                  label={count === 1 ? office : `${office} · ${count}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  }

  return (
    <Grid item xs={12} md={6}>
      <InfoCard title="Team Composition" variant="gridItem">
        {body}
      </InfoCard>
    </Grid>
  );
}
